import type {
  BulkPriceResult,
  BulkPriceUpdateInput,
  MediaRef,
  PriceImportResult,
  ProductVariant,
  SeoMeta,
  SpecGroup,
  TranslatableString,
} from '@geoic/shared';
import { api, type ListResult } from '@/lib/api-client.js';
import { createContentApi, type ContentRecord } from '@/features/content/content-api.js';

export type ProductRecord = ContentRecord & {
  title: TranslatableString;
  shortDescription: TranslatableString;
  description: TranslatableString;
  brandId: string | null;
  categoryIds: string[];
  coverImage: MediaRef | null;
  gallery: MediaRef[];
  video: { source: 'youtube' | 'upload'; url: string; poster: MediaRef | null } | null;
  specGroups: SpecGroup[];
  documents: Array<{ title: TranslatableString; file: MediaRef; order: number }>;
  variants: ProductVariant[];
  priceMin: number | null;
  priceMax: number | null;
  hasPrice: boolean;
  availabilityAgg: 'in_stock' | 'on_order' | 'out_of_stock';
  variantCount: number;
  isFeatured: boolean;
  seo: SeoMeta;
};

export type CategoryRecord = ContentRecord & {
  name: TranslatableString;
  description: TranslatableString;
  parentId: string | null;
  image: MediaRef | null;
  icon: string | null;
  productCount: number;
  seo: SeoMeta;
};

/** Brend nomi TARJIMA QILINMAYDI — «DJI» hamma tilda bir xil. */
export type BrandRecord = ContentRecord & {
  name: string;
  logo: MediaRef | null;
  description: TranslatableString;
};

export const productsApi = createContentApi<ProductRecord>('/products');

/**
 * Katalog kategoriyalari — endpoint `product-categories`.
 *
 * Adminda yo'l `/categories`, API da esa `product-categories`: kontentda
 * ham kategoriyalar bor (yangiliklar, hujjatlar) va ular chalkashmasligi
 * kerak. Bu farq bir marta o'tkazib yuborilgan va tanlov ro'yxati
 * jimgina bo'sh qolgan edi — endi shu izoh bilan qulflangan.
 */
export const categoriesApi = createContentApi<CategoryRecord>('/product-categories');
export const brandsApi = createContentApi<BrandRecord>('/brands');

export type PriceHistoryRow = {
  _id: string;
  productId: string;
  variantId: string;
  productTitle: string;
  variantTitle: string;
  sku: string | null;
  oldPrice: number | null;
  newPrice: number | null;
  source: 'manual' | 'bulk' | 'import' | 'migration';
  userEmail: string;
  batchId: string | null;
  note: string;
  createdAt: string;
};

/**
 * Narxlar moduli.
 *
 * `bulk` ikki marta chaqiriladi: avval `dryRun: true` bilan (oldindan
 * ko'rish), keyin xodim tasdiqlagach `dryRun: false` bilan. Ikkinchi
 * chaqiruvda kirish AYNAN bir xil bo'lishi kerak — aks holda xodim
 * ko'rgan ro'yxat bilan yozilgan narxlar farq qilardi.
 */
export const pricesApi = {
  bulk: (body: BulkPriceUpdateInput) =>
    api.post<{ data: BulkPriceResult }>('/prices/bulk', body).then((r) => r.data),

  history: (params: { page?: number; limit?: number; productId?: string; batchId?: string }) =>
    api.get<ListResult<PriceHistoryRow>>('/prices/history', params),

  exportFile: () => api.download('/prices/export'),

  import: (file: File, options: { dryRun: boolean; note: string }) => {
    const form = new FormData();
    form.append('file', file);
    return api
      .upload<{ data: PriceImportResult }>(
        `/prices/import?dryRun=${options.dryRun}&note=${encodeURIComponent(options.note)}`,
        form,
      )
      .then((r) => r.data);
  },
};
